import i18n from "i18next";
import {initReactI18next} from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import commonEs from "./resources/es/common.json";
import navigationEs from "./resources/es/navigation.json";
import notificationsEs from "./resources/es/notifications.json";
import errorsEs from "./resources/es/errors.json";
import homeEs from "./resources/es/home.json";
import aboutEs from "./resources/es/about.json";
import authEs from "./resources/es/auth.json";
import leaguesEs from "./resources/es/leagues.json";
import lineupEs from "./resources/es/lineup.json";
import questionsEs from "./resources/es/questions.json";
import standingsEs from "./resources/es/standings.json";
import statsEs from "./resources/es/stats.json";
import palmaresEs from "./resources/es/palmares.json";
import prefsEs from "./resources/es/prefs.json";
import notFoundEs from "./resources/es/notFound.json";
import adminEs from "./resources/es/admin.json";

import commonEn from "./resources/en/common.json";
import navigationEn from "./resources/en/navigation.json";
import notificationsEn from "./resources/en/notifications.json";
import errorsEn from "./resources/en/errors.json";
import homeEn from "./resources/en/home.json";
import aboutEn from "./resources/en/about.json";
import authEn from "./resources/en/auth.json";
import leaguesEn from "./resources/en/leagues.json";
import lineupEn from "./resources/en/lineup.json";
import questionsEn from "./resources/en/questions.json";
import standingsEn from "./resources/en/standings.json";
import statsEn from "./resources/en/stats.json";
import palmaresEn from "./resources/en/palmares.json";
import prefsEn from "./resources/en/prefs.json";
import notFoundEn from "./resources/en/notFound.json";
import adminEn from "./resources/en/admin.json";

import commonEu from "./resources/eu/common.json";
import navigationEu from "./resources/eu/navigation.json";
import notificationsEu from "./resources/eu/notifications.json";
import errorsEu from "./resources/eu/errors.json";
import homeEu from "./resources/eu/home.json";
import aboutEu from "./resources/eu/about.json";
import authEu from "./resources/eu/auth.json";
import leaguesEu from "./resources/eu/leagues.json";
import lineupEu from "./resources/eu/lineup.json";
import questionsEu from "./resources/eu/questions.json";
import standingsEu from "./resources/eu/standings.json";
import statsEu from "./resources/eu/stats.json";
import palmaresEu from "./resources/eu/palmares.json";
import prefsEu from "./resources/eu/prefs.json";
import notFoundEu from "./resources/eu/notFound.json";
import adminEu from "./resources/eu/admin.json";

import commonCa from "./resources/ca/common.json";
import navigationCa from "./resources/ca/navigation.json";
import notificationsCa from "./resources/ca/notifications.json";
import errorsCa from "./resources/ca/errors.json";
import homeCa from "./resources/ca/home.json";
import aboutCa from "./resources/ca/about.json";
import authCa from "./resources/ca/auth.json";
import leaguesCa from "./resources/ca/leagues.json";
import lineupCa from "./resources/ca/lineup.json";
import questionsCa from "./resources/ca/questions.json";
import standingsCa from "./resources/ca/standings.json";
import statsCa from "./resources/ca/stats.json";
import palmaresCa from "./resources/ca/palmares.json";
import prefsCa from "./resources/ca/prefs.json";
import notFoundCa from "./resources/ca/notFound.json";
import adminCa from "./resources/ca/admin.json";


export const SUPPORTED_LANGUAGES = ["es", "en", "eu", "ca"] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Key used by i18next-browser-languagedetector when caching to localStorage.
const I18N_LANG_STORAGE_KEY = "i18nextLng";

/**
 * Read the saved language synchronously from localStorage at module load
 * time. This is what we pass as the explicit `lng` option to i18n.init() so
 * that the user's choice is honored on every page reload, even if the
 * LanguageDetector somehow fails to apply it in time. Returns undefined when
 * nothing is saved yet (first-time visitors), so the `fallbackLng: "es"`
 * still kicks in.
 */
const readSavedLanguage = (): SupportedLanguage | undefined => {
    try {
        const raw = window.localStorage.getItem(I18N_LANG_STORAGE_KEY);
        if (!raw) return undefined;
        const base = raw.split("-")[0] as SupportedLanguage;
        return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? base : undefined;
    } catch {
        return undefined;
    }
};

const resources = {
    es: {
        common: commonEs,
        navigation: navigationEs,
        notifications: notificationsEs,
        errors: errorsEs,
        home: homeEs,
        about: aboutEs,
        auth: authEs,
        leagues: leaguesEs,
        lineup: lineupEs,
        questions: questionsEs,
        standings: standingsEs,
        stats: statsEs,
        palmares: palmaresEs,
        prefs: prefsEs,
        notFound: notFoundEs,
        admin: adminEs,
    },
    en: {
        common: commonEn,
        navigation: navigationEn,
        notifications: notificationsEn,
        errors: errorsEn,
        home: homeEn,
        about: aboutEn,
        auth: authEn,
        leagues: leaguesEn,
        lineup: lineupEn,
        questions: questionsEn,
        standings: standingsEn,
        stats: statsEn,
        palmares: palmaresEn,
        prefs: prefsEn,
        notFound: notFoundEn,
        admin: adminEn,
    },
    eu: {
        common: commonEu,
        navigation: navigationEu,
        notifications: notificationsEu,
        errors: errorsEu,
        home: homeEu,
        about: aboutEu,
        auth: authEu,
        leagues: leaguesEu,
        lineup: lineupEu,
        questions: questionsEu,
        standings: standingsEu,
        stats: statsEu,
        palmares: palmaresEu,
        prefs: prefsEu,
        notFound: notFoundEu,
        admin: adminEu,
    },
    ca: {
        common: commonCa,
        navigation: navigationCa,
        notifications: notificationsCa,
        errors: errorsCa,
        home: homeCa,
        about: aboutCa,
        auth: authCa,
        leagues: leaguesCa,
        lineup: lineupCa,
        questions: questionsCa,
        standings: standingsCa,
        stats: statsCa,
        palmares: palmaresCa,
        prefs: prefsCa,
        notFound: notFoundCa,
        admin: adminCa,
    }
};

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        // Spanish is the default for first-time visitors. Browser language is
        // intentionally NOT consulted — the language picker (top nav + Prefs)
        // lets users opt into other languages, and that choice persists in
        // localStorage so subsequent visits honor it.
        //
        // The top nav is built on Cloudscape buttons whose `href` renders as
        // plain anchor tags, so navigating between pages triggers a full
        // reload and re-runs i18n.init(). To avoid the user's saved language
        // ever being lost mid-flow, we read localStorage *synchronously* here
        // and pass it as the explicit `lng`. The LanguageDetector is still
        // wired below so `i18n.changeLanguage(...)` keeps caching to
        // localStorage automatically.
        lng: readSavedLanguage(),
        fallbackLng: "es",
        supportedLngs: [...SUPPORTED_LANGUAGES],
        // Strip region subtags (e.g. "en-US" -> "en") so our short codes still match.
        load: "languageOnly",
        nonExplicitSupportedLngs: true,
        defaultNS: "common",
        interpolation: {
            escapeValue: false,
        },
        detection: {
            // Only consult localStorage. We never want to guess from the
            // browser — first-time visitors should always start in Spanish.
            order: ["localStorage"],
            caches: ["localStorage"],
            lookupLocalStorage: I18N_LANG_STORAGE_KEY,
        },
        returnNull: false,
    });

// Belt-and-suspenders: i18next normally writes to localStorage on every
// language change via the detector's cache, but we also do it explicitly here
// so the value is guaranteed to be on disk before any subsequent navigation.
i18n.on("languageChanged", (lng) => {
    try {
        if (lng) {
            const base = lng.split("-")[0];
            if ((SUPPORTED_LANGUAGES as readonly string[]).includes(base)) {
                window.localStorage.setItem(I18N_LANG_STORAGE_KEY, base);
            }
        }
    } catch {
        // Ignore — private mode / disabled storage shouldn't break i18n.
    }
});

export default i18n;
